import { trackEvent, landingAttribution } from "@/api/tracking";
import { getAttributionTracker } from "@/utils/attributionTracker";
import {
  TrackingEvent,
  TrackingEventRequest,
  TrackingEventType,
} from "@/types/tracking.types";
import type {
  LandingAttributionData,
  LandingAttrFingerprint,
} from "@/types/landingAttribution.types";
import utils from "@/utils/utils";
import { TrackingData } from "../types/attributionTracker.types";

const HOME_PAGE_VIEW_KEY = "tracking_home_page_viewed";
const TEMP_DEVICE_UID_KEY = "__uid";
const AUUID_KEY = "__auuid";

/**
 * 埋点服务 - 统一处理所有埋点上报
 */
class TrackingService {
  private fingerprint: TrackingData | null = null;
  private isInitialized = false;

  /**
   * 初始化 - 获取指纹和sessionId
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      const tracker = getAttributionTracker();
      const trackingData = tracker.getTrackingData();

      this.fingerprint = trackingData;
      this.isInitialized = true;
    } catch (error) {
      console.error("TrackingService 初始化失败:", error);
    }
  }

  /**
   * 确保已初始化
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }
  }

  /**
   * 获取指纹
   */
  async getFingerprint(): Promise<TrackingData | null> {
    await this.ensureInitialized();
    return this.fingerprint || null;
  }

  /**
   * 生成 UUID
   */
  private generateUUID(): string {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * 持久化存储到 localStorage + cookie（3天有效期）
   */
  private persistValue(key: string, value: string): void {
    utils.setObject(key, value);
    utils.setCookie(key, value, 7);
  }

  /**
   * 从 localStorage 或 cookie 中读取值
   */
  private getPersistedValue(key: string): string | null {
    return localStorage.getItem(key) || utils.getCookie(key) || null;
  }

  /**
   * 获取或创建临时设备 UID（localStorage + cookie 持久化）
   */
  private getOrCreateTempUid(): string {
    let uid = this.getPersistedValue(TEMP_DEVICE_UID_KEY);
    if (!uid) {
      uid = this.generateUUID();
    }
    this.persistValue(TEMP_DEVICE_UID_KEY, uid);
    return uid;
  }

  /**
   * 获取设备品牌和型号
   */
  private getDeviceInfo(): { brand?: string; model?: string } {
    const ua = navigator.userAgent;
    let brand: string | undefined;
    let model: string | undefined;

    // iOS 设备
    if (/iPhone/.test(ua)) {
      brand = "Apple";
      model = "iPhone";
      // 尝试获取具体型号（iPhone X, iPhone 11 等）
      const iphoneMatch = ua.match(/iPhone\s?(\d+[,_]\d+)?/);
      if (iphoneMatch) {
        model = iphoneMatch[0].replace(/[,_]/g, " ");
      }
    } else if (/iPad/.test(ua)) {
      brand = "Apple";
      model = "iPad";
    } else if (/iPod/.test(ua)) {
      brand = "Apple";
      model = "iPod";
    }
    // Android 设备
    else if (/Android/.test(ua)) {
      // Android User Agent 格式通常为：
      // Mozilla/5.0 (Linux; Android 10; SM-G973F) ...
      // Mozilla/5.0 (Linux; Android 11; Pixel 5) ...

      // 尝试匹配设备型号
      const androidMatch = ua.match(/Android[^;]*;\s*([^)]+)\)/);
      if (androidMatch && androidMatch[1]) {
        const deviceInfo = androidMatch[1].trim();

        // 常见品牌识别
        const brands: Record<string, string[]> = {
          Samsung: ["SM-", "SAMSUNG", "Galaxy"],
          Huawei: ["HUAWEI", "HW-", "Honor", "HONOR"],
          Xiaomi: ["Xiaomi", "MI ", "Redmi", "POCO"],
          Oppo: ["OPPO", "CPH", "PCHM", "PCRT"],
          Vivo: ["vivo", "V"],
          OnePlus: ["OnePlus", "ONE A"],
          Google: ["Pixel"],
          Motorola: ["moto", "Motorola"],
          Nokia: ["Nokia"],
          Sony: ["Sony", "SO-"],
          LG: ["LG-"],
          HTC: ["HTC"],
        };

        // 识别品牌
        for (const [brandName, patterns] of Object.entries(brands)) {
          if (patterns.some(pattern => deviceInfo.includes(pattern))) {
            brand = brandName;
            model = deviceInfo;
            break;
          }
        }

        // 如果没有匹配到品牌，直接使用设备信息作为型号
        if (!brand) {
          // 尝试分割获取品牌和型号
          const parts = deviceInfo.split(/\s+/);
          if (parts.length >= 2) {
            brand = parts[0];
            model = deviceInfo;
          } else {
            model = deviceInfo;
          }
        }
      }
    }
    // Windows Phone
    else if (/Windows Phone/.test(ua)) {
      brand = "Microsoft";
      const wpMatch = ua.match(/Windows Phone[^;]*;\s*([^)]+)\)/);
      if (wpMatch && wpMatch[1]) {
        model = wpMatch[1].trim();
      } else {
        model = "Windows Phone";
      }
    }

    return { brand, model };
  }

  /**
   * 获取完整的基础参数
   */
  private getBaseParams(): Partial<TrackingEventRequest> {
    const env = utils.getEnvInfo?.() || {};
    const urlParams = new URLSearchParams(window.location.search);

    // 获取路径（不含查询参数）
    const path = window.location.pathname;

    // 判断终端类型
    let terminalType = "BROWSER";
    if (window.matchMedia("(display-mode: standalone)").matches) {
      terminalType = "PWA";
    } else if ((window as any).isNativeApp) {
      terminalType = "APP";
    }

    // 判断用户来源
    let userSource: string | undefined;
    const source = urlParams.get("source") || urlParams.get("utm_source");
    if (source) {
      const normalized = source.toUpperCase();
      if (normalized.includes("FACEBOOK") || normalized.includes("FB")) {
        userSource = "FACEBOOK";
      } else if (normalized.includes("TIKTOK") || normalized.includes("TT")) {
        userSource = "TIKTOK";
      } else if (normalized.includes("GOOGLE") || normalized.includes("GG")) {
        userSource = "GOOGLE";
      }
    }

    // 判断代理类型
    let agentType = "NORMAL";
    if (
      urlParams.get("gclid") ||
      urlParams.get("fbclid") ||
      utils.getCookie("_fbp") ||
      utils.getCookie("_fbc") ||
      utils.getCookie("_ttclid") ||
      utils.getCookie("_gclid")
    ) {
      agentType = "ADS";
    }

    // 获取操作系统
    let operatingSystem = "OTHER";
    if (env.os === "iOS") {
      operatingSystem = "IOS";
    } else if (env.os === "Android") {
      operatingSystem = "ANDROID";
    }

    // 获取浏览器类型
    let browserType = "OTHER";
    if (env.browser) {
      const browserMap: Record<string, string> = {
        Chrome: "CHROME",
        Safari: "SAFARI",
        Firefox: "FIREFOX",
        Edge: "EDGE",
        FacebookInApp: "FACEBOOK_WEBVIEW",
        InstagramInApp: "INSTAGRAM_WEBVIEW",
        TelegramInApp: "TELEGRAM_WEBVIEW",
        TikTokInApp: "TIKTOK_WEBVIEW",
        UnknownWebView: "OTHER_WEBVIEW",
      };
      browserType = browserMap[env.browser] || "OTHER";
    }

    // 获取网络类型
    let networkType = "UNKNOWN";
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection;
    if (connection) {
      // 优先判断连接类型（wifi/cellular等）
      if (connection.type === "wifi" || connection.type === "ethernet") {
        networkType = "WIFI";
      } else if (connection.effectiveType) {
        // 根据有效连接类型判断
        const type = connection.effectiveType.toLowerCase();
        if (type === "4g") networkType = "_4G";
        else if (type === "3g") networkType = "_3G";
        else if (type === "2g") networkType = "_2G";
        else if (type === "slow-2g") networkType = "_2G";
      }
    }

    // 获取设备信息
    const deviceInfo = this.getDeviceInfo();

    return {
      auuid: utils.getObject("__auuid"),
      uid: utils.getObject("__uid"),
      fullUrl: window.location.href,
      domain: window.location.hostname,
      path, // 添加路径参数
      referrer: document.referrer || undefined,
      userSource,
      utmSource: urlParams.get("utm_source") || undefined,
      utmMedium: urlParams.get("utm_medium") || undefined,
      utmCampaign: urlParams.get("utm_campaign") || undefined,
      utmContent: urlParams.get("utm_content") || undefined,
      utmTerm: urlParams.get("utm_term") || undefined,
      terminalType,
      operatingSystem,
      browserType,
      isWebView: env.isWebView || env.context === "InAppWebView",
      deviceBrand: deviceInfo.brand,
      deviceModel: deviceInfo.model,
      userAgent: navigator.userAgent,
      agentType,
      agentId: urlParams.get("agent_id") || urlParams.get("ref") || undefined,
      networkType,
      screenResolution: `${screen.width}x${screen.height}`,
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      language:
        utils.getObject("currentLanguage") ||
        navigator.language ||
        (navigator as any).userLanguage,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      fbp: utils.getCookie("_fbp"),
      fbc: utils.getCookie("_fbc"),
      gclid: urlParams.get("gclid") || undefined,
      ttclid: urlParams.get("ttclid") || undefined,
    };
  }

  /**
   * 通用埋点上报方法
   */
  async track(
    event: TrackingEvent,
    eventType: TrackingEventType,
    customParams?: Partial<TrackingEventRequest>
  ): Promise<void> {
    try {
      await this.ensureInitialized();

      const baseParams: TrackingEventRequest = {
        event,
        eventType,
        browserFingerprint: utils.getObject("BrowserFingerprintId"),
        fingerprintData: this.fingerprint?.fingerprint || null,
        session: this.fingerprint?.session || null,
        ...this.getBaseParams(),
        ...customParams,
      };

      await trackEvent(baseParams);
    } catch (error) {
      console.error(`埋点上报失败 [${eventType}]:`, error);
    }
  }

  /**
   * 首页浏览 - 相同指纹只回传一次
   */
  async trackHomePageView(): Promise<void> {
    await this.ensureInitialized();
    const browserFingerprintId = utils.getObject("BrowserFingerprintId");
    const key = `${HOME_PAGE_VIEW_KEY}_${browserFingerprintId}`;
    const hasViewed = utils.getObject(key);

    if (!hasViewed) {
      await this.track(TrackingEvent.PV, TrackingEventType.PV);
      utils.setObject(key, true);
    }
  }

  /**
   * 投放落地页PV
   */
  async trackAdLandingPagePV(eventDetail?: string): Promise<void> {
    await this.track(TrackingEvent.PV, TrackingEventType.PV, {
      eventDetail,
    });
  }

  /**
   * 非投放落地页PV
   */
  async trackNormalLandingPagePV(eventDetail?: string): Promise<void> {
    await this.track(TrackingEvent.PV, TrackingEventType.PV, {
      eventDetail,
    });
  }

  /**
   * 落地页点击 - 点击安装按钮且在内置浏览器中
   */
  async trackLandingPageClick(): Promise<void> {
    await this.track(TrackingEvent.PWA_INSTALL_CLICK, TrackingEventType.EVENT);
  }
  async trackRechargeSuccessClick(message: string): Promise<void> {
    await this.track(TrackingEvent.PV, TrackingEventType.PV, {
      eventDetail: message,
    });
  }
  /**
   * 注册开始
   */
  async trackRegisterStart(): Promise<void> {
    await this.track(TrackingEvent.APPLY_REGISTER, TrackingEventType.EVENT);
  }

  /**
   * 注册成功
   */
  async trackRegisterSuccess(userId?: string): Promise<void> {
    await this.track(TrackingEvent.REGISTER_SUCCESS, TrackingEventType.EVENT, {
      userId,
    });
  }

  /**
   * 注册失败
   */
  async trackRegisterFailed(message: string): Promise<void> {
    await this.track(TrackingEvent.REGISTER_FAILED, TrackingEventType.EVENT, {
      message,
    });
  }

  /**
   * 登陆开始
   */
  async trackLoginStart(): Promise<void> {
    await this.track(TrackingEvent.APPLY_LOGIN, TrackingEventType.EVENT);
  }

  /**
   * 登陆成功
   */
  async trackLoginSuccess(userId?: string): Promise<void> {
    await this.track(TrackingEvent.LOGIN_SUCCESS, TrackingEventType.EVENT, {
      userId,
    });
  }

  /**
   * 登陆失败
   */
  async trackLoginFailed(message: string): Promise<void> {
    await this.track(TrackingEvent.LOGIN_FAILED, TrackingEventType.EVENT, {
      message,
    });
  }

  /**
   * PWA安装提示
   */
  async trackPwaInstallPrompt(): Promise<void> {
    await this.track(TrackingEvent.PV, TrackingEventType.PV);
  }

  /**
   * PWA安装完成 - appinstalled事件
   */
  async trackPwaAppInstalled(): Promise<void> {
    await this.track(
      TrackingEvent.PWA_INSTALL_SUCCESS,
      TrackingEventType.EVENT
    );
  }

  /**
   * 登陆页面PV
   */
  async trackLoginPagePV(): Promise<void> {
    await this.track(TrackingEvent.PV, TrackingEventType.PV, {
      eventDetail: "LOGIN_PAGE",
    });
  }

  /**
   * 注册页面PV
   */
  async trackRegisterPagePV(): Promise<void> {
    await this.track(TrackingEvent.PV, TrackingEventType.PV, {
      eventDetail: "REGISTER_PAGE",
    });
  }

  /**
   * 落地页浏览器埋点回传
   */
  async trackLandingAttribution(bfId?: string): Promise<void> {
    await this.ensureInitialized();

    const urlParams = new URLSearchParams(window.location.search);

    // auuid: URL中存在则使用，不存在则新建UUID，同时持久化
    const auuid = urlParams.get("auuid") || this.generateUUID();
    this.persistValue(AUUID_KEY, auuid);

    // uid: 临时设备ID，localStorage + cookie 持久化
    const uid = this.getOrCreateTempUid();

    // landingUrl: 落地页完整URL
    const landingUrl = window.location.href;

    // UTM参数
    const utmSource = urlParams.get("utm_source") || "";
    const utmMedium = urlParams.get("utm_medium") || "";
    const utmCampaign = urlParams.get("utm_campaign") || "";
    const utmContent = urlParams.get("utm_content") || "";
    const utmTerm = urlParams.get("utm_term") || "";

    // 从tracker获取fingerprint信息并重组
    const fp = this.fingerprint?.fingerprint;

    const fingerprint: LandingAttrFingerprint = {
      userAgent: navigator.userAgent,
      screen: fp?.screen || {
        width: screen.width,
        height: screen.height,
        availWidth: screen.availWidth,
        availHeight: screen.availHeight,
        colorDepth: screen.colorDepth,
        pixelRatio: window.devicePixelRatio || 1,
        orientation: "unknown",
      },
      webglInfo: fp?.webglInfo
        ? {
            renderer: fp.webglInfo.renderer,
            vendor: fp.webglInfo.vendor,
            version: fp.webglInfo.version,
          }
        : null,
      locale: {
        language: fp?.browser?.language || navigator.language,
        timezone:
          fp?.timezone?.id || Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: fp?.timezone?.offset ?? new Date().getTimezoneOffset(),
      },
      hardware: fp?.hardware || {
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
        deviceMemory:
          (navigator as Navigator & { deviceMemory?: number }).deviceMemory ||
          0,
        maxTouchPoints: navigator.maxTouchPoints || 0,
      },
    };

    const data: LandingAttributionData = {
      auuid,
      bfId: bfId || "",
      uid,
      landingUrl,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmTerm,
      fingerprint,
    };

    await landingAttribution(data);
  }
}

// 创建单例
const trackingService = new TrackingService();

// 导出单例
export default trackingService;
export { trackingService };
